import { avatarSrc } from '../../utils/employeeAvatar';
import { useMemo, useState } from 'react';
import {
  Modal, ModalDialog, Box, Stack, Typography, Input, List, ListItem,
  ListItemButton, Checkbox, Chip, Avatar, Button, IconButton,
  CircularProgress, Divider,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';

import { APICreateGroupChat } from '../../api';

const fio = (c) => `${c.last_name || ''} ${c.name || ''}`.trim() || `ID ${c.id}`;
const initials = (c) => ((c.last_name?.[0] || '') + (c.name?.[0] || '')).toUpperCase();

export default function CreateGroupModal({ open, onClose, contacts = [], loading, token, onCreated }) {
  const [step, setStep] = useState(0);            // 0 — выбор, 1 — название
  const [query, setQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  const selected = useMemo(
    () => contacts.filter((c) => selectedIds.includes(c.id)),
    [contacts, selectedIds],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) => fio(c).toLowerCase().includes(q));
  }, [contacts, query]);

  const reset = () => { setStep(0); setQuery(''); setSelectedIds([]); setName(''); setCreating(false); };
  const close = () => { reset(); onClose?.(); };

  const toggle = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const goNext = () => {
    setName(selected.map(fio).join(', '));   // имя по умолчанию — ФИО через запятую
    setStep(1);
  };

  const create = async () => {
    if (!selectedIds.length) return;
    try {
      setCreating(true);
      const res = await APICreateGroupChat((name || '').trim(), selectedIds, token);
      if (res?.chat_id) { await onCreated?.(res.chat_id); reset(); }
    } catch (e) {
      console.error('Ошибка создания группы:', e);
      alert('Не удалось создать группу');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal open={open} onClose={close} sx={{ zIndex: 10012 }}>
      <ModalDialog sx={{ p: 0, width: 460, maxWidth: '94vw', overflow: 'hidden', borderRadius: 'xl' }}>
        {/* Шапка */}
        <Box sx={{ p: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
          {step === 1 && (
            <IconButton size="sm" variant="plain" onClick={() => setStep(0)}>
              <ArrowBackRoundedIcon />
            </IconButton>
          )}
          <GroupsRoundedIcon color="primary" />
          <Typography level="h4" sx={{ flex: 1 }}>
            {step === 0 ? 'Новая группа' : 'Название группы'}
          </Typography>
          <IconButton size="sm" variant="plain" onClick={close}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <Box sx={{ position: 'relative' }}>
          <AnimatePresence mode="wait" initial={false}>
            {step === 0 ? (
              <motion.div
                key="pick"
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ type: 'spring', stiffness: 320, damping: 32 }}
              >
                {/* Чипы выбранных (можно убрать кликом) */}
                <AnimatePresence initial={false}>
                  {selected.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, p: 2, pb: 1 }}>
                        {selected.map((c) => (
                          <motion.div
                            key={c.id} layout
                            initial={{ scale: 0.6, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.6, opacity: 0 }}
                          >
                            <Chip
                              variant="soft" color="primary"
                              endDecorator={<CloseRoundedIcon sx={{ fontSize: 14 }} />}
                              onClick={() => toggle(c.id)}
                              sx={{ cursor: 'pointer' }}
                            >
                              {fio(c)}
                            </Chip>
                          </motion.div>
                        ))}
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Поиск */}
                <Box sx={{ px: 2, pb: 1 }}>
                  <Input
                    size="sm"
                    placeholder="Поиск людей..."
                    startDecorator={<SearchRoundedIcon />}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </Box>

                {/* Список контактов с галочками */}
                <Box sx={{ maxHeight: 280, overflowY: 'auto', px: 1 }}>
                  {loading ? (
                    <Stack sx={{ alignItems: 'center', py: 4 }}><CircularProgress size="sm" /></Stack>
                  ) : (
                    <List sx={{ '--ListItem-paddingX': '0.75rem' }}>
                      {filtered.map((c) => {
                        const checked = selectedIds.includes(c.id);
                        return (
                          <ListItem key={c.id}>
                            <ListItemButton
                              selected={checked}
                              onClick={() => toggle(c.id)}
                              sx={{ borderRadius: 'md', gap: 1.25 }}
                            >
                              <Checkbox checked={checked} readOnly variant="soft" />
                              <Avatar src={avatarSrc(c)} size="sm">{initials(c)}</Avatar>
                              <Typography level="title-sm" noWrap>{fio(c)}</Typography>
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                      {filtered.length === 0 && (
                        <Typography level="body-sm" sx={{ textAlign: 'center', py: 3, color: 'text.tertiary' }}>
                          Никого не найдено
                        </Typography>
                      )}
                    </List>
                  )}
                </Box>

                <Divider />
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                    Выбрано: {selected.length}
                  </Typography>
                  <Button onClick={goNext} disabled={selected.length === 0}>
                    Продолжить
                  </Button>
                </Box>
              </motion.div>
            ) : (
              <motion.div
                key="name"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 40 }}
                transition={{ type: 'spring', stiffness: 320, damping: 32 }}
              >
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <Avatar sx={{ '--Avatar-size': '64px', background: 'linear-gradient(135deg,#6366f1,#a855f7)', color: '#fff' }}>
                      <GroupsRoundedIcon />
                    </Avatar>
                  </Box>

                  <Typography level="body-sm" sx={{ mb: 0.5, color: 'text.secondary' }}>
                    Название
                  </Typography>
                  <Input
                    autoFocus
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Название группы"
                  />

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 2 }}>
                    {selected.map((c) => (
                      <Chip key={c.id} size="sm" variant="soft" color="neutral">{fio(c)}</Chip>
                    ))}
                  </Box>
                </Box>

                <Divider />
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Button variant="plain" color="neutral" onClick={() => setStep(0)} startDecorator={<ArrowBackRoundedIcon />}>
                    Назад
                  </Button>
                  <Button onClick={create} loading={creating} disabled={!selectedIds.length}>
                    Создать
                  </Button>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </ModalDialog>
    </Modal>
  );
}